import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Upload,
  CheckCircle,
  AlertTriangle,
  X,
  Loader2,
  Mail,
  FileText,
} from 'lucide-react';

type Step = 'loading' | 'ready' | 'uploading' | 'success' | 'error';

interface SelectedFile {
  file: File;
  name: string;
  size: number;
}

export default function UploadSpamSamples() {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [contactName, setContactName] = useState('');
  const [files, setFiles] = useState<SelectedFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedCount, setUploadedCount] = useState(0);

  const apiBase = import.meta.env.VITE_API_URL || 'https://pax8lic-api.azurewebsites.net/api';
  const MAX_FILES = 10;
  const MAX_SIZE_MB = 1;

  useEffect(() => {
    if (token) checkStatus();
  }, [token]);

  async function checkStatus() {
    try {
      const res = await fetch(`${apiBase}/spam-upload/${token}`);
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'This link is no longer valid.');
        setStep('error');
        return;
      }

      setClientName(data.clientName);
      setContactName(data.contactName);
      setStep('ready');
    } catch {
      setError('Unable to connect. Please try again later.');
      setStep('error');
    }
  }

  function validateFile(file: File): string | null {
    const ext = file.name.toLowerCase().split('.').pop();
    if (ext !== 'eml' && ext !== 'msg') return `"${file.name}" is not a supported format. Only .eml and .msg files are accepted.`;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) return `"${file.name}" exceeds the ${MAX_SIZE_MB}MB size limit.`;
    return null;
  }

  function addFiles(newFiles: FileList | File[]) {
    setUploadError(null);
    const toAdd: SelectedFile[] = [];
    for (const file of Array.from(newFiles)) {
      const err = validateFile(file);
      if (err) { setUploadError(err); return; }
      if (files.length + toAdd.length >= MAX_FILES) { setUploadError(`Maximum ${MAX_FILES} files allowed.`); return; }
      // Deduplicate by name
      if (!files.some(f => f.name === file.name) && !toAdd.some(f => f.name === file.name)) {
        toAdd.push({ file, name: file.name, size: file.size });
      }
    }
    setFiles(prev => [...prev, ...toAdd]);
  }

  function removeFile(name: string) {
    setFiles(prev => prev.filter(f => f.name !== name));
    setUploadError(null);
  }

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  }, [files]);

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip data URL prefix: "data:...;base64,"
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleUpload() {
    if (files.length === 0) return;
    setStep('uploading');
    setUploadError(null);

    try {
      const filePayloads = await Promise.all(
        files.map(async f => ({
          name: f.name,
          contentBase64: await fileToBase64(f.file),
        }))
      );

      const res = await fetch(`${apiBase}/spam-upload/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ files: filePayloads }),
      });

      const data = await res.json();
      if (!res.ok) {
        setUploadError(data.error || 'Upload failed');
        setStep('ready');
        return;
      }

      setUploadedCount(files.length);
      setStep('success');
    } catch {
      setUploadError('Upload failed. Please try again.');
      setStep('ready');
    }
  }

  function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  // ── Loading ──
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="h-8 w-8 animate-spin text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Validating upload link...</p>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link Unavailable</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // ── Success ──
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-4">
            {uploadedCount} spam sample{uploadedCount > 1 ? 's' : ''} uploaded successfully.
          </p>
          <p className="text-gray-500 text-sm">
            Your IT team has been notified and will analyze these samples. You can close this page.
          </p>
        </div>
      </div>
    );
  }

  // ── Ready / Uploading ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 text-center">
          <Mail className="h-10 w-10 mx-auto mb-3 opacity-90" />
          <h1 className="text-xl font-bold">Upload Spam Samples</h1>
          <p className="text-red-100 text-sm mt-1">{clientName}</p>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-gray-600 text-sm">
            Hi {contactName}, please upload the spam emails you've been receiving so our team can investigate.
          </p>

          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
              dragOver ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-red-300 hover:bg-red-50/50'
            }`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">Drag & drop .eml or .msg files here</p>
            <p className="text-gray-400 text-sm mt-1">or click to browse</p>
            <p className="text-gray-400 text-xs mt-2">Up to {MAX_FILES} files, {MAX_SIZE_MB}MB each</p>
            <input
              id="file-input"
              type="file"
              accept=".eml,.msg"
              multiple
              className="hidden"
              onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">{files.length} file{files.length > 1 ? 's' : ''} selected</p>
              {files.map(f => (
                <div key={f.name} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                  <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-700 flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-gray-400">{formatSize(f.size)}</span>
                  <button onClick={() => removeFile(f.name)} className="text-gray-400 hover:text-red-500">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {uploadError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{uploadError}</p>
            </div>
          )}

          {/* Upload button */}
          <button
            onClick={handleUpload}
            disabled={files.length === 0 || step === 'uploading'}
            className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {step === 'uploading' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Upload {files.length > 0 ? `${files.length} Sample${files.length > 1 ? 's' : ''}` : 'Samples'}
              </>
            )}
          </button>

          {/* Instructions */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-amber-800 mb-2">How to save spam emails:</h3>
            <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
              <li><strong>Outlook Desktop:</strong> Drag the email from your inbox to your Desktop</li>
              <li><strong>Outlook Web:</strong> Open email → ⋯ menu → Download</li>
              <li><strong>Gmail:</strong> Open email → ⋯ menu → Download message</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
